import type { ProductTemplate } from './types';
import {
  KS714_COLOUR,
  ks714Geometry,
  ks714Model3d,
  SPA_MANAGER_SIZE,
} from './spaManagerCabinet';

/**
 * Catalogue of prefabricated products shipped with StenalCAD.
 * Dimensions are in millimetres. These are simple parametric footprints;
 * users can add detailed products by importing their own DXF/DWG drawings.
 */
/** The generic lighting products (category 'lighting'), so the Lighting tools
 *  work offline and on an empty catalogue: a LED strip, a spot and a fibre
 *  projector, each in 2700 K white (the default) and in RGB. A strip's power is
 *  per metre (`powerPerMeter`), a spot's or a projector's its own (`lightPower`). */
export const BUILTIN_LIGHTING: ProductTemplate[] = [
  {
    id: 'builtin-led-strip', name: 'LED strip 24 V · 5 W/m · 2700 K', category: 'lighting',
    nameI18n: { en: 'LED strip 24 V · 5 W/m · 2700 K', it: 'Striscia LED 24 V · 5 W/m · 2700 K', fr: 'Ruban LED 24 V · 5 W/m · 2700 K' },
    width: 1000, depth: 10, color: '#ffb020', builtin: true, properties: [],
    lightKind: 'strip', lightColour: '2700K', powerPerMeter: 5, lightBeamAngle: 120, lightLumens: 450,
  },
  {
    id: 'builtin-led-strip-rgb', name: 'LED strip 24 V · 5 W/m · RGB', category: 'lighting',
    nameI18n: { en: 'LED strip 24 V · 5 W/m · RGB', it: 'Striscia LED 24 V · 5 W/m · RGB', fr: 'Ruban LED 24 V · 5 W/m · RVB' },
    width: 1000, depth: 10, color: '#ffb020', builtin: true, properties: [],
    lightKind: 'strip', lightColour: 'RGB', powerPerMeter: 5, lightBeamAngle: 120, lightLumens: 250,
  },
  {
    id: 'builtin-spot', name: 'Spot Ø68 · 3 W · 2700 K', category: 'lighting',
    nameI18n: { en: 'Spot Ø68 · 3 W · 2700 K', it: 'Faretto Ø68 · 3 W · 2700 K', fr: 'Spot Ø68 · 3 W · 2700 K' },
    width: 68, depth: 68, color: '#ffb020', builtin: true, properties: [],
    lightKind: 'spot', lightColour: '2700K', lightPower: 3, lightBeamAngle: 36, lightLumens: 250,
  },
  {
    id: 'builtin-spot-rgb', name: 'Spot Ø68 · 3 W · RGB', category: 'lighting',
    nameI18n: { en: 'Spot Ø68 · 3 W · RGB', it: 'Faretto Ø68 · 3 W · RGB', fr: 'Spot Ø68 · 3 W · RVB' },
    width: 68, depth: 68, color: '#ffb020', builtin: true, properties: [],
    lightKind: 'spot', lightColour: 'RGB', lightPower: 3, lightBeamAngle: 36, lightLumens: 150,
  },
  {
    id: 'builtin-fibre-projector', name: 'Fibre projector · 16 W · 2700 K', category: 'lighting',
    nameI18n: { en: 'Fibre projector · 16 W · 2700 K', it: 'Proiettore per fibre ottiche · 16 W · 2700 K', fr: 'Projecteur à fibres optiques · 16 W · 2700 K' },
    width: 300, depth: 200, color: '#3a3d46', builtin: true, properties: [],
    lightKind: 'projector', lightColour: '2700K', lightPower: 16,
  },
  {
    id: 'builtin-fibre-projector-rgb', name: 'Fibre projector · 16 W · RGB', category: 'lighting',
    nameI18n: { en: 'Fibre projector · 16 W · RGB', it: 'Proiettore per fibre ottiche · 16 W · RGB', fr: 'Projecteur à fibres optiques · 16 W · RVB' },
    width: 300, depth: 200, color: '#3a3d46', builtin: true, properties: [],
    lightKind: 'projector', lightColour: 'RGB', lightPower: 16,
  },
];

export const BUILTIN_PRODUCTS: ProductTemplate[] = [
  {
    id: 'builtin-sauna-2x2',
    name: 'Sauna',
    nameI18n: { en: 'Sauna', it: 'Sauna', fr: 'Sauna' },
    category: 'sauna',
    width: 2000,
    depth: 2000,
    color: '#c8853c',
    description: 'Compact 4-person prefabricated sauna cabin.',
    builtin: true,
    autoBenches: true,
    properties: [
      { key: 'heater', label: 'Heater', value: 'Electric 6 kW' },
      { key: 'power', label: 'Power', value: 6, unit: 'kW' },
      { key: 'wood', label: 'Cladding', value: 'Nordic spruce' },
    ],
  },
  {
    id: 'builtin-steam-2x2',
    name: 'Steam Room',
    nameI18n: { en: 'Steam Room', it: 'Bagno turco', fr: 'Hammam' },
    category: 'steamroom',
    width: 2000,
    depth: 2000,
    color: '#2f79b9',
    description: 'Premium steam room with aroma dosing and mood lighting.',
    builtin: true,
    autoBenches: true,
    properties: [
      { key: 'capacity', label: 'Capacity', value: 4, unit: 'persons' },
      { key: 'generator', label: 'Steam generator', value: '9 kW' },
      { key: 'power', label: 'Power', value: 9, unit: 'kW' },
      { key: 'finish', label: 'Finish', value: 'Polished concrete' },
    ],
  },
  {
    id: 'builtin-sauna-poly',
    name: 'Polygonal Sauna',
    nameI18n: { en: 'Polygonal Sauna', it: 'Sauna poligonale', fr: 'Sauna polygonal' },
    category: 'sauna',
    width: 2000,
    depth: 2000,
    color: '#c8853c',
    description: 'Free-form sauna: click to draw the wall outline, right-click to close.',
    builtin: true,
    autoBenches: true,
    polyRoom: true,
    properties: [
      { key: 'heater', label: 'Heater', value: 'Electric 6 kW' },
      { key: 'wood', label: 'Cladding', value: 'Nordic spruce' },
    ],
  },
  {
    id: 'builtin-steam-poly',
    name: 'Polygonal Steam Room',
    nameI18n: { en: 'Polygonal Steam Room', it: 'Bagno turco poligonale', fr: 'Hammam polygonal' },
    category: 'steamroom',
    width: 2000,
    depth: 2000,
    color: '#2f79b9',
    description: 'Free-form steam room: click to draw the wall outline, right-click to close.',
    builtin: true,
    autoBenches: true,
    polyRoom: true,
    properties: [
      { key: 'generator', label: 'Steam generator', value: '9 kW' },
      { key: 'finish', label: 'Finish', value: 'Polished concrete' },
    ],
  },
  {
    id: 'builtin-steam-cube',
    name: 'Steam Cube',
    nameI18n: { en: 'Steam Cube', it: 'Steam Cube', fr: 'Steam Cube' },
    category: 'steamcube',
    width: 500,
    depth: 500,
    color: '#3c3d42',
    description: 'Decorative steam stone block. Drop it into a steam room; it sits on top of the benches.',
    builtin: true,
    properties: [
      { key: 'material', label: 'Material', value: 'Basalt stone' },
    ],
  },
  {
    id: 'builtin-saltwall',
    name: 'Salt wall',
    nameI18n: { en: 'Salt wall', it: 'Parete di sale', fr: 'Mur de sel' },
    category: 'saltwall',
    width: 1053,
    depth: 191,
    color: '#f6c4d2',
    description: 'Salt wall element: two fixed brown end frames with a pink body that stretches between them. Drag the side handles to set its length.',
    builtin: true,
    properties: [
      { key: 'material', label: 'Material', value: 'Himalayan salt' },
    ],
  },
  {
    id: 'builtin-canalina',
    name: 'Drain Channel',
    nameI18n: { en: 'Drain Channel', it: 'Canalina di scarico', fr: 'Caniveau de drainage' },
    category: 'canalina',
    width: 700,
    depth: 65,
    color: '#ffff00',
    description: 'Drain channel: a yellow channel that stretches along its length. Drag the side handles to set its length; the height stays fixed.',
    builtin: true,
    properties: [],
  },
  {
    id: 'builtin-drain',
    name: 'Drain',
    nameI18n: { en: 'Drain', it: 'Piletta di scarico', fr: 'Siphon de sol' },
    category: 'drain',
    width: 100,
    depth: 100,
    color: '#ffff00',
    description: 'Floor drain grate (Ø100). Drag a corner handle to resize.',
    builtin: true,
    ports: [{ id: 'outlet', name: 'Outlet', nx: 0, ny: 0, type: 'drainage' }],
    properties: [],
  },
  {
    id: 'builtin-island-shower',
    name: 'Island shower',
    nameI18n: { en: 'Island shower', it: 'Island shower', fr: 'Island shower' },
    category: 'shower',
    // Total footprint: 3 heads × 430 + 2 gaps × 370 = 2030 mm wide, 430 mm deep.
    width: 2030,
    depth: 430,
    color: '#3cc89a',
    description: 'Island shower with three independent overhead showerheads (single, dual-vertical, dual-horizontal), spaced 370 mm apart.',
    builtin: true,
    parts: [
      {
        id: 'p0',
        name: 'Single Rain Head',
        nameI18n: { en: 'Single Rain Head', it: 'Soffione singolo', fr: 'Pomme de douche simple' },
        width: 430,
        depth: 430,
        color: '#72c8c8',
        offset: { x: -800, y: 0 },
        geometry: {
          bounds: { min: { x: -215, y: -215 }, max: { x: 215, y: 215 } },
          entities: [
            { kind: 'hatch', loops: [[{ x: -175, y: -175 }, { x: 175, y: -175 }, { x: 175, y: 175 }, { x: -175, y: 175 }]], solid: true, color: '#72c8c8e6' },
            { kind: 'circle', center: { x: 0, y: 0 }, radius: 42, color: '#1c1c1c' },
          ],
        },
        preparations: [],
      },
      {
        id: 'p1',
        name: 'Dual Vertical Head',
        nameI18n: { en: 'Dual Vertical Head', it: 'Soffione doppio verticale', fr: 'Pomme de douche double verticale' },
        width: 430,
        depth: 430,
        color: '#e87860',
        offset: { x: 0, y: 0 },
        geometry: {
          bounds: { min: { x: -215, y: -215 }, max: { x: 215, y: 215 } },
          entities: [
            { kind: 'hatch', loops: [[{ x: -175, y: -175 }, { x: 175, y: -175 }, { x: 175, y: 175 }, { x: -175, y: 175 }]], solid: true, color: '#e87860e6' },
            { kind: 'circle', center: { x: 0, y: -85 }, radius: 42, color: '#1c1c1c' },
            { kind: 'circle', center: { x: 0, y: 85 }, radius: 42, color: '#1c1c1c' },
          ],
        },
        preparations: [],
      },
      {
        id: 'p2',
        name: 'Dual Horizontal Head',
        nameI18n: { en: 'Dual Horizontal Head', it: 'Soffione doppio orizzontale', fr: 'Pomme de douche double horizontale' },
        width: 430,
        depth: 430,
        color: '#74cc60',
        offset: { x: 800, y: 0 },
        geometry: {
          bounds: { min: { x: -215, y: -215 }, max: { x: 215, y: 215 } },
          entities: [
            { kind: 'hatch', loops: [[{ x: -175, y: -175 }, { x: 175, y: -175 }, { x: 175, y: 175 }, { x: -175, y: 175 }]], solid: true, color: '#74cc60e6' },
            { kind: 'circle', center: { x: -85, y: 0 }, radius: 42, color: '#1c1c1c' },
            { kind: 'circle', center: { x: 85, y: 0 }, radius: 42, color: '#1c1c1c' },
          ],
        },
        preparations: [],
      },
    ],
    connections: [],
    properties: [
      { key: 'flow', label: 'Flow rate', value: '3 × 12 L/min' },
    ],
  },
  {
    id: 'builtin-paradise-shower',
    name: 'Paradise',
    nameI18n: { en: 'Paradise', it: 'Paradise', fr: 'Paradise' },
    category: 'shower',
    // Total footprint: 4 heads × 430 + 3 gaps × 369 = 2827 mm wide, 430 mm deep.
    width: 2827,
    depth: 430,
    color: '#3cc89a',
    description: 'Island shower with four independent overhead showerheads (single, dual-vertical, dual-horizontal, rain panel), spaced 369 mm apart.',
    builtin: true,
    parts: [
      {
        id: 'p0',
        name: 'Single Rain Head',
        nameI18n: { en: 'Single Rain Head', it: 'Soffione singolo', fr: 'Pomme de douche simple' },
        width: 430,
        depth: 430,
        color: '#72c8c8',
        offset: { x: -1198.5, y: 0 },
        geometry: {
          bounds: { min: { x: -215, y: -215 }, max: { x: 215, y: 215 } },
          entities: [
            // Inner face (cyan fill)
            { kind: 'hatch', loops: [[{ x: -175, y: -175 }, { x: 175, y: -175 }, { x: 175, y: 175 }, { x: -175, y: 175 }]], solid: true, color: '#72c8c8e6' },
            // Single nozzle circle
            { kind: 'circle', center: { x: 0, y: 0 }, radius: 42, color: '#1c1c1c' },
          ],
        },
        preparations: [],
      },
      {
        id: 'p1',
        name: 'Dual Vertical Head',
        nameI18n: { en: 'Dual Vertical Head', it: 'Soffione doppio verticale', fr: 'Pomme de douche double verticale' },
        width: 430,
        depth: 430,
        color: '#e87860',
        offset: { x: -399.5, y: 0 },
        geometry: {
          bounds: { min: { x: -215, y: -215 }, max: { x: 215, y: 215 } },
          entities: [
            { kind: 'hatch', loops: [[{ x: -175, y: -175 }, { x: 175, y: -175 }, { x: 175, y: 175 }, { x: -175, y: 175 }]], solid: true, color: '#e87860e6' },
            { kind: 'circle', center: { x: 0, y: -85 }, radius: 42, color: '#1c1c1c' },
            { kind: 'circle', center: { x: 0, y: 85 }, radius: 42, color: '#1c1c1c' },
          ],
        },
        preparations: [],
      },
      {
        id: 'p2',
        name: 'Dual Horizontal Head',
        nameI18n: { en: 'Dual Horizontal Head', it: 'Soffione doppio orizzontale', fr: 'Pomme de douche double horizontale' },
        width: 430,
        depth: 430,
        color: '#74cc60',
        offset: { x: 399.5, y: 0 },
        geometry: {
          bounds: { min: { x: -215, y: -215 }, max: { x: 215, y: 215 } },
          entities: [
            { kind: 'hatch', loops: [[{ x: -175, y: -175 }, { x: 175, y: -175 }, { x: 175, y: 175 }, { x: -175, y: 175 }]], solid: true, color: '#74cc60e6' },
            { kind: 'circle', center: { x: -85, y: 0 }, radius: 42, color: '#1c1c1c' },
            { kind: 'circle', center: { x: 85, y: 0 }, radius: 42, color: '#1c1c1c' },
          ],
        },
        preparations: [],
      },
      {
        id: 'p3',
        name: 'Rain Panel',
        nameI18n: { en: 'Rain Panel', it: 'Pannello pioggia', fr: 'Panneau de pluie' },
        width: 430,
        depth: 430,
        color: '#f0c860',
        offset: { x: 1198.5, y: 0 },
        geometry: {
          bounds: { min: { x: -215, y: -215 }, max: { x: 215, y: 215 } },
          entities: [
            // Yellow frame
            { kind: 'hatch', loops: [[{ x: -175, y: -175 }, { x: 175, y: -175 }, { x: 175, y: 175 }, { x: -175, y: 175 }]], solid: true, color: '#f0c860e6' },
            // Dark spray plate
            { kind: 'hatch', loops: [[{ x: -130, y: -130 }, { x: 130, y: -130 }, { x: 130, y: 130 }, { x: -130, y: 130 }]], solid: true, color: '#1c1c1ce8' },
            // 4×4 sample nozzle dots (yellow, so they show as bright spots)
            ...(() => {
              const dots: { kind: 'circle'; center: { x: number; y: number }; radius: number; color: string }[] = [];
              const step = 60, r = 10;
              for (let row = -1; row <= 2; row++) for (let col = -1; col <= 2; col++) {
                dots.push({ kind: 'circle', center: { x: -90 + col * step, y: -90 + row * step }, radius: r, color: '#f0c860' });
              }
              return dots;
            })(),
          ],
        },
        preparations: [],
      },
    ],
    connections: [],
    properties: [
      { key: 'flow', label: 'Flow rate', value: '4 × 12 L/min' },
    ],
  },
  {
    id: 'builtin-heated-bench-a',
    name: 'Heated Bench A',
    nameI18n: { en: 'Heated Bench A', it: 'Panca riscaldata A', fr: 'Banc chauffant A' },
    category: 'heatedbench',
    width: 2000,
    depth: 500,
    color: '#c8955a',
    description: 'Heated bench variant A. Drag the side handles to set its length; depth stays fixed at 500 mm.',
    builtin: true,
    properties: [
      { key: 'type', label: 'Type', value: 'A' },
      { key: 'power', label: 'Power', value: 2, unit: 'kW' },
    ],
  },
  {
    id: 'builtin-heated-bench-b',
    name: 'Heated Bench B',
    nameI18n: { en: 'Heated Bench B', it: 'Panca riscaldata B', fr: 'Banc chauffant B' },
    category: 'heatedbench',
    width: 2000,
    depth: 500,
    color: '#c8955a',
    description: 'Heated bench variant B. Drag the side handles to set its length; depth stays fixed at 500 mm.',
    builtin: true,
    properties: [
      { key: 'type', label: 'Type', value: 'B' },
      { key: 'power', label: 'Power', value: 2, unit: 'kW' },
    ],
  },
  {
    id: 'builtin-sauna-bench',
    name: 'Sauna Bench',
    nameI18n: { en: 'Sauna Bench', it: 'Panca per sauna', fr: 'Banc de sauna' },
    category: 'saunabench',
    width: 2000,
    depth: 396, // Σ slats (4×90) + Σ gaps (3×12)
    color: '#c9a36a',
    description: 'Slatted sauna bench that follows a polyline path (like a heated bench). Drag the side handles to set its length; pick a bench-slat preset or configure the slats independently, as for a sauna-room bench.',
    builtin: true,
    slats: [90, 90, 90, 90],
    gaps: [12, 12, 12],
    slatThickness: [28, 28, 28, 28],
    overlap: 0,
    properties: [
      { key: 'material', label: 'Material', value: 'Abachi slats' },
    ],
  },
  {
    id: 'builtin-foot-spa',
    name: 'Foot Spa',
    nameI18n: { en: 'Foot Spa', it: 'Pediluvio', fr: 'Bain de pieds' },
    category: 'shower',
    width: 863,
    depth: 600,
    color: '#c8a878',
    description: 'Two-part foot spa: basin with controls (723 × 600 mm) and glass screen panel (140 × 600 mm).',
    builtin: true,
    parts: [
      {
        id: 'p0',
        name: 'Basin & Controls',
        nameI18n: { en: 'Basin & Controls', it: 'Vasca e comandi', fr: 'Bassin et commandes' },
        width: 723,
        depth: 600,
        color: '#c8a878',
        offset: { x: -70, y: 0 },
        geometry: {
          bounds: { min: { x: -361.5, y: -300 }, max: { x: 361.5, y: 300 } },
          entities: [
            // Beige surround
            { kind: 'hatch', loops: [[{ x: -361.5, y: -300 }, { x: 361.5, y: -300 }, { x: 361.5, y: 300 }, { x: -361.5, y: 300 }]], solid: true, color: '#c8a878d0' },
            // Water basin (left ~60 % of part, 50 mm inset on three sides)
            { kind: 'hatch', loops: [[{ x: -311.5, y: -250 }, { x: 111.5, y: -250 }, { x: 111.5, y: 250 }, { x: -311.5, y: 250 }]], solid: true, color: '#6eb4d8c0' },
            // Divider between basin and controls
            { kind: 'line', a: { x: 111.5, y: -300 }, b: { x: 111.5, y: 300 }, color: '#5a4a2a' },
            // Large control knob
            { kind: 'circle', center: { x: 236.5, y: -75 }, radius: 35, color: '#5a4a2a' },
            // Small control knob
            { kind: 'circle', center: { x: 236.5, y: 75 }, radius: 22, color: '#5a4a2a' },
          ],
        },
        preparations: [],
      },
      {
        id: 'p1',
        name: 'Glass Screen',
        nameI18n: { en: 'Glass Screen', it: 'Schermo in vetro', fr: 'Paroi en verre' },
        width: 140,
        depth: 600,
        color: '#b0c8d8',
        offset: { x: 361.5, y: 0 },
        geometry: {
          bounds: { min: { x: -70, y: -300 }, max: { x: 70, y: 300 } },
          entities: [
            // Translucent glass fill
            { kind: 'hatch', loops: [[{ x: -70, y: -300 }, { x: 70, y: -300 }, { x: 70, y: 300 }, { x: -70, y: 300 }]], solid: true, color: '#b0c8d870' },
            // Glass symbol: X diagonals
            { kind: 'line', a: { x: -70, y: -300 }, b: { x: 70, y: 300 }, color: '#6090a8' },
            { kind: 'line', a: { x: 70, y: -300 }, b: { x: -70, y: 300 }, color: '#6090a8' },
          ],
        },
        preparations: [],
      },
    ],
    connections: [],
    properties: [
      { key: 'type', label: 'Type', value: 'Foot spa with glass screen' },
    ],
  },
  // The electrical schematic's boxes (TODO.md, decisions 22 / 23): no
  // preparations and no connection points — each becomes electrical through
  // the connections drawn to it.
  // The Spa Manager is built in an Elettra KS714.30 cabinet (spaManagerCabinet.ts).
  {
    id: 'builtin-spamanager',
    name: 'Spa Manager',
    nameI18n: { en: 'Spa Manager', it: 'Spa Manager', fr: 'Spa Manager' },
    category: 'spamanager',
    junctionBox: true,
    width: SPA_MANAGER_SIZE[0],
    depth: SPA_MANAGER_SIZE[1],
    height: SPA_MANAGER_SIZE[2],
    color: KS714_COLOUR,
    description: 'Electrical control panel of the spa: feeds and drives every room’s heaters, lights and devices. Built in an Elettra KS714.30 cabinet (Kappa series, IP43, glass door, 700 × 300 × 1400, RAL 7035) on its 100 mm Kappa plinth: 1500 tall overall. Stands on the floor.',
    builtin: true,
    properties: [],
    geometry: ks714Geometry(),
    model3d: ks714Model3d(),
  },
  {
    id: 'builtin-junction-box',
    name: 'Junction box',
    nameI18n: { en: 'Junction box', it: 'Scatola di derivazione', fr: 'Boîte de dérivation' },
    category: 'spamanager',
    junctionBox: true,
    width: 300,
    depth: 300,
    height: 150,
    color: '#9aa0a6',
    description: 'Electrical junction box. Dropped inside a room it sits on the room’s ceiling, against a wall it stands upright on it, elsewhere on the floor.',
    builtin: true,
    properties: [],
    onRoomTop: true,
    wallMountCentre: 1500,
  },
];

// The generic lighting products ride with the rest (`BUILTIN_LIGHTING`).
BUILTIN_PRODUCTS.push(...BUILTIN_LIGHTING);
